import { useQuery } from "@tanstack/react-query";
import { SIGNING_STATUS_LABELS, type SigningPackageView } from "@wms/domain";
import { Alert, Button, Descriptions, Form, Input, Select, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { formatDate, formatDateTime, formatQty } from "../api/format";

interface Provider {
  code: string;
  label: string;
  available: boolean;
  reason: string | null;
}

/** A party has its own words: «Подписано» is one signature, not the state of the whole package. */
const PARTY_LABELS: Record<string, string> = {
  pending: "Ожидает",
  signed: "Подписано",
  rejected: "Отказ",
  unknown: "Результат неизвестен",
  failed: "Ошибка",
};

interface SnapshotLine {
  line_no: number;
  description: string;
  quantity: string;
  unit_code: string;
  /** CW31-B09: the Russian word («шт») the server puts next to the stable code («piece»). */
  unit_label?: string;
  rate: string;
  net: string;
  tax: string;
  gross: string;
}

/**
 * C05 / W16 / 13.2–13.4: the exact version that is being signed — act, register appendix, hashes, parties — with
 * «Подписать» (a way the provider really offers) and «Отказать» with a reason. Nothing here pretends a method works.
 */
export function SigningView({
  view,
  providers,
  onSign,
  onReject,
  canSign,
  canReject,
  busy,
}: {
  view: SigningPackageView;
  providers?: Provider[];
  onSign?: (body: { provider: string; signature: string; signer_name?: string; expected_hash: string }) => void;
  /** The refusal names the version it refers to, like the signature (8.3). */
  onReject?: (reason: string, expectedHash: string) => void;
  canSign: boolean;
  canReject: boolean;
  busy?: boolean;
}) {
  const [provider, setProvider] = useState<string | undefined>();
  const [signature, setSignature] = useState("");
  const [signerName, setSignerName] = useState("");
  const [reason, setReason] = useState("");
  const act = (view.act ?? {}) as { number?: string; date?: string; lines?: SnapshotLine[]; totals?: Record<string, string>; operator?: { name?: string }; customer?: { name?: string } };
  const register = (view.register ?? {}) as { number?: string; lines?: SnapshotLine[]; totals?: Record<string, string> };
  const available = (providers ?? []).filter((p) => p.available);
  const lineColumns = [
    { title: "№", dataIndex: "line_no", width: 50 },
    { title: "Услуга", dataIndex: "description" },
    // CW31: «160», not «160.000» — the signed snapshot keeps its own text, only the screen reads it as a number
    { title: "Кол-во", dataIndex: "quantity", align: "right" as const, render: (v: unknown) => formatQty(v) },
    // CW31-B09: the word of the unit, the code only where a server does not give it yet
    { title: "Ед.", dataIndex: "unit_code", render: (v: string, r: SnapshotLine) => r.unit_label ?? v },
    { title: "Ставка", dataIndex: "rate", align: "right" as const },
    { title: "Без налога", dataIndex: "net", align: "right" as const },
    { title: "Налог", dataIndex: "tax", align: "right" as const },
    { title: "С налогом", dataIndex: "gross", align: "right" as const },
  ];
  return (
    <div>
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="Документ">АВР {act.number ?? ""} от {formatDate(act.date)}, версия {view.version}</Descriptions.Item>
        <Descriptions.Item label="Стороны">{act.operator?.name ?? "Исполнитель"} → {act.customer?.name ?? "Заказчик"}</Descriptions.Item>
        <Descriptions.Item label="Состояние">
          <Tag>{SIGNING_STATUS_LABELS[view.status] ?? view.status}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Хеш версии">
          <Typography.Text code copyable style={{ fontSize: 12 }}>{view.package_hash}</Typography.Text>
        </Descriptions.Item>
        {view.expires_at && <Descriptions.Item label="Ссылка действует до">{formatDateTime(view.expires_at)}</Descriptions.Item>}
      </Descriptions>
      <Typography.Title level={5}>АВР</Typography.Title>
      <Table rowKey="line_no" size="small" pagination={false} dataSource={act.lines ?? []} columns={lineColumns} scroll={{ x: true }} />
      {act.totals && (
        <Typography.Paragraph style={{ textAlign: "right", marginTop: 8 }}>
          Итого с налогом: <b>{act.totals.gross}</b>
        </Typography.Paragraph>
      )}
      {register.lines && (
        <>
          <Typography.Title level={5}>Реестр-приложение {register.number ?? ""}</Typography.Title>
          <Table rowKey="line_no" size="small" pagination={false} dataSource={register.lines} columns={lineColumns} scroll={{ x: true }} />
        </>
      )}
      <Typography.Title level={5} style={{ marginTop: 16 }}>
        Подписи
      </Typography.Title>
      <Table
        rowKey="side"
        size="small"
        pagination={false}
        dataSource={view.parties}
        columns={[
          { title: "Сторона", dataIndex: "side", render: (v: string) => (v === "operator" ? "Исполнитель" : "Клиент") },
          { title: "Состояние", dataIndex: "status", render: (v: string) => PARTY_LABELS[v] ?? SIGNING_STATUS_LABELS[v] ?? v },
          { title: "Подписант", dataIndex: "signer_name", render: (v: string | null) => v ?? "" },
          { title: "Когда", dataIndex: "signed_at", render: (v: string | null) => (v ? formatDateTime(v) : "") },
          { title: "Проверка", dataIndex: "verification_status", render: (v: string) => ({ valid: "Подпись проверена", invalid: "Не прошла проверку", unavailable: "Не удалось проверить", not_verified: "" })[v] ?? v },
          { title: "Причина отказа", dataIndex: "reject_reason", render: (v: string | null) => v ?? "" },
        ]}
      />
      {canSign && view.can_sign && (
        <div style={{ marginTop: 16, border: "1px solid var(--wms-border)", borderRadius: 10, padding: 12 }}>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            Подписать
          </Typography.Title>
          {available.length === 0 ? (
            <Alert
              type="warning"
              showIcon
              message="Электронное подписание пока недоступно"
              description={
                <>
                  {(providers ?? []).map((p) => (
                    <div key={p.code}>
                      {p.label}: {p.reason}
                    </div>
                  ))}
                  <div>Доступный путь: подписание на бумаге или по согласованному с исполнителем каналу.</div>
                </>
              }
            />
          ) : (
            <Form layout="vertical">
              <Form.Item label="Способ">
                <Select value={provider} onChange={setProvider} options={available.map((p) => ({ value: p.code, label: p.label }))} placeholder="Выберите способ" style={{ maxWidth: 360 }} />
              </Form.Item>
              <Form.Item label="Подписант (ФИО)">
                <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} style={{ maxWidth: 360 }} />
              </Form.Item>
              <Form.Item label="Подпись (данные провайдера)" extra="Подпись проверяется сервером: совпадение БИН стороны, версия документа и целостность">
                <Input.TextArea value={signature} onChange={(e) => setSignature(e.target.value)} rows={2} data-testid="sign-signature" />
              </Form.Item>
              <Button type="primary" disabled={!provider || !signature} loading={busy} onClick={() => onSign?.({ provider: provider!, signature, signer_name: signerName || undefined, expected_hash: view.package_hash })} data-testid="sign-submit">
                Подписать эту версию
              </Button>
            </Form>
          )}
        </div>
      )}
      {canSign && !view.can_sign && view.why_not && <Alert type="info" showIcon style={{ marginTop: 12 }} message={view.why_not} />}
      {canReject && ["ready", "invited", "awaiting", "verifying", "partially_signed"].includes(view.status) && (
        <div style={{ marginTop: 12 }}>
          <Space.Compact style={{ width: "100%", maxWidth: 560 }}>
            <Input placeholder="Основание отказа" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="sign-reject-reason" />
            <Button danger disabled={reason.trim().length < 3} loading={busy} onClick={() => onReject?.(reason.trim(), view.package_hash)} data-testid="sign-reject">
              Отказать
            </Button>
          </Space.Compact>
        </div>
      )}
    </div>
  );
}

export function useProviders(fetcher: () => Promise<Provider[]>, enabled = true) {
  return useQuery({ queryKey: ["signing", "providers"], queryFn: fetcher, enabled, staleTime: 60_000 });
}

export type { Provider };
